import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import pluralize from 'pluralize';
import { Fragment, ReactNode, useMemo, VFC } from 'react';
import styled from 'styled-components';

import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { valueTransformer } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';

const SelectedFieldAndButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FieldName = styled.dt`
  ${paragraphMixins.mdBold}

  margin-top: ${Spacing[2]};
`;

const FieldValue = styled.dd.attrs<{ hasError: boolean }>(({ hasError }) => ({
  'aria-invalid': hasError,
}))<{ hasError: boolean }>`
  ${paragraphMixins.md}
  margin-top: ${Spacing[1]};
  color: ${({ hasError }) => (hasError ? Color.TextDanger : 'inherit')};
  overflow-x: auto;
`;

interface FieldsListProps {
  fields: Record<string, string | null> | [string, string | null][];
  fieldKey: string;
  editable: boolean;
  placeholderEmptyText?: string;
  toggleEditPanel: () => void;
  tokensToDisplayValue: Record<string, string>;
  errors: WorkflowError[];
  formatValue?: (fieldKey: string, fieldValue: string | null) => ReactNode;
}

/**
 * Renders a fields list to display
 * @param {string} fields - Fields to display in list format
 * @param {string} fieldKey - key of fields for rendering error message
 * @param {boolean} editable - whether fields are editable
 * @param {WorkflowError[]} errors  - Errors associated with fields
 * @param {() => void} toggleEditPanel - function to pop up edit fields panel
 */
const FieldsList: VFC<FieldsListProps> = ({
  editable,
  errors,
  fieldKey: baseFieldKey,
  fields,
  placeholderEmptyText = 'No fields selected',
  tokensToDisplayValue,
  formatValue,
  toggleEditPanel,
}) => {
  const { getDisplayValue } = valueTransformer(tokensToDisplayValue);
  const fieldEntries: [string, string | null][] = useMemo(() => {
    const entries: [string, string | null][] =
      fields == null ? [] : Array.isArray(fields) ? fields : Object.entries(fields);
    return entries.map(([fieldKey, fieldValue]) => [
      fieldKey,
      fieldValue && getDisplayValue(fieldValue),
    ]);
  }, [fields, getDisplayValue]);
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<`${string}.${string}`>(errors);

  const buttonToEdit = (
    <Button
      variant='basic'
      width='auto'
      disabled={!editable}
      id='fields-list__toggle-edit-panel'
      data-test='fields-list__toggle-edit-panel'
      aria-label='Edit Fields'
      suffixIcon={<EditIcon />}
      onClick={toggleEditPanel}
    >
      Fields
    </Button>
  );
  if (fieldEntries != null && fieldEntries.length > 0) {
    return (
      <>
        <SelectedFieldAndButton>
          <Paragraph>
            {`${fieldEntries.length} ${pluralize('field', fieldEntries.length)} selected`}
          </Paragraph>
          {buttonToEdit}
        </SelectedFieldAndButton>
        <dl role='list'>
          {fieldEntries.map(([fieldKey, fieldValue], idx) => (
            <Fragment key={fieldKey}>
              <FieldName data-test={`fields-list__field-${idx}__label`}>{fieldKey}</FieldName>
              <FieldValue
                hasError={hasErrorFor(`${baseFieldKey}.${fieldKey}`)}
                data-test={`fields-list__field-${idx}__value`}
              >
                {formatValue != null ? formatValue(fieldKey, fieldValue) : fieldValue}
              </FieldValue>
              {editable && renderErrorMessageFor(`${baseFieldKey}.${fieldKey}`, { narrow: true })}
            </Fragment>
          ))}
        </dl>
      </>
    );
  } else {
    return (
      <SelectedFieldAndButton>
        <Paragraph>{placeholderEmptyText}</Paragraph>
        {buttonToEdit}
      </SelectedFieldAndButton>
    );
  }
};

export default FieldsList;
